import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from '../../translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { WithMessages } from '../common-behaviors/with-messages.mixin';
import { BillingChannel } from './billing-channel.resource';
import { BillingMode } from './billing-mode.resource';
import { BusinessUnit } from './business-unit.resource';
import { Country } from './country.resource';
import { Currency } from './currency.resource';
import { Enum } from './enum.resource';
import { Firm } from './firm.resource';
import { Job } from './job.resource';
import { OfferBillingPolicy, OfferBillingPolicyConfig } from './offer-billing-policy';
import {
  OfferCapability,
} from './offer-capability.resource';
import { PackOffer } from './pack-offer.resource';
import { Period } from './period.resource';
import { Platform } from './platform.resource';
import { Product } from './product.resource';
import { CreditSetup } from './credit-setup.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  NumberFilter,
  StringFilter,
  ResourceListFilter,
} from 'src/app/filter/filter';
import { AtomService } from '../atom.service';
import { MessageConfig } from './message.resource';
import {
  BillingChannelConfig,
  FirmJobConfig,
  PlatformConfig,
  CountryConfig,
  ProductConfig,
  BillingModeConfig,
  PeriodConfig,
  CurrencyConfig,
  CreditsetupConfig,
  FirmConfig,
} from 'src/app/shared/tools/search/search-config';
import { FormGroup } from '@angular/forms';

// const OfferMixinBase = WithMessages(Validable(AtomResource));
const OfferMixinBase = WithMessages(AtomResource);

@Resource({
  type: 'offers',
})
export class Offer extends OfferMixinBase {
  @Attribute() name?: Translatable;
  @Attribute({ key: 'commercial_name' }) commercialName?: Translatable;
  @Attribute() price?: number;
  @Attribute({ key: 'trial_price' }) trialPrice?: number;
  @Attribute({ key: 'trial_iteration' }) trialIteration?: number;
  @Attribute({ key: 'subscription_context' }) subscriptionContext?:
    | 'service'
    | 'product'
    | 'offer'
    | 'none';
  @Attribute({ key: 'recurring_iteration' }) recurringIteration?: number;
  @Attribute({ key: 'duration_iteration' }) durationIteration?: number;
  @Attribute({ key: 'validity_tz' }) validityTz?: string;
  @Attribute() validity?: string;
  @Attribute() data?: Record<string, unknown>;

  @Relationship() businessunits?: BusinessUnit[];
  @Relationship() billingchannel?: BillingChannel;
  @Relationship() billingmode?: BillingMode;
  @Relationship() currency?: Currency;
  @Relationship() trialperiod?: Period;
  @Relationship() durationperiod?: Period;
  @Relationship() enduserproviderjob?: Job;
  @Relationship() billingproviderjob?: Job;
  @Relationship() offercapabilities?: OfferCapability[];
  @Relationship() packoffers?: PackOffer[];
  @Relationship() recurringperiod?: Period;
  @Relationship() platform?: Platform;
  @Relationship() handlerfirm?: Firm;
  @Relationship() product?: Product;
  @Relationship() country?: Country;
  @Relationship() enumsubscriptioncontext?: Enum;
  @Relationship() offerbillingpolicy?: OfferBillingPolicy[];
  @Relationship() creditsetup?: CreditSetup;
  // @Relationship() messages?: Message[];
}
export class OfferConfig implements ResourceConfig {
  tables = {
    includes: [
      'country',
      'currency',
      'billingchannel',
      'billingmode',
      'enduserproviderjob.firm.headquarterscountry',
      'enduserproviderjob.activity',
      'billingproviderjob.firm.headquarterscountry',
      'billingproviderjob.activity',
      'product.service',
      'product.country',
      'packoffers',
      'recurringperiod',
      'businessunits',
      'trialperiod',
      'durationperiod',
      'handlerfirm.headquarterscountry',
      'platform',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'status',
        header: 'Validity',
        displayed: true,
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
          {
            path: ['commercialName'],
            class: 'mat-body-1',
          },
        ],
        header: 'Name / Commercial Name',
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'name',
      },
      {
        columnDef: 'price',
        displayedDatas: [
          {
            path: ['price'],
            class: 'mat-title',
            addUnit: {
              path: 'currency.id',
            },
          },
        ],
        header: 'Price',
        displayed: true,
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'billingmode',
        displayedDatas: [
          {
            path: ['billingmode.name'],
            class: 'mat-body-1',
          },
          {
            path: ['recurringperiod.name'],
            class: 'mat-body-2',
            nullIfEmpty: true,
          },
        ],
        header: 'Billing Mode / Recurring',
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'billingmode.name',
      },
      {
        columnDef: 'enduserprovider',
        displayedDatas: [
          {
            path: ['enduserproviderjob.firm.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: [
              'enduserproviderjob.firm.headquarterscountry.id',
              'enduserproviderjob.activity.name',
            ],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Enduser Provider',
        translatable: true,
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'enduserproviderjob.firm.id',
          icon: 'signal_cellular_alt',
        },
        sort: 'enduserproviderjob.firm.name',
      },
      {
        columnDef: 'billingprovider',
        displayedDatas: [
          {
            path: ['billingproviderjob.firm.name'],
            class: 'mat-body-2',
          },
          {
            path: ['billingproviderjob.firm.headquarterscountry.id'],
            class: 'mat-body-1',
          },
        ],
        header: 'Billing Provider',
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'billingproviderjob.firm.id',
          icon: 'business',
        },
        sort: 'billingprovider.firm.name',
      },
      {
        columnDef: 'billingchannel',
        displayedDatas: [
          {
            path: ['billingchannel.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Billing Channel',
        displayed: false,
        sort: 'billingchannel.name',
      },
      {
        columnDef: 'product',
        displayedDatas: [
          {
            path: ['product.name'],
            class: 'mat-body-2',
          },
          {
            path: ['product.country.id'],
            class: 'mat-body-1',
          },
        ],
        header: 'Product',
        link: {
          resource: 'products',
          filter: 'product.id',
          icon: 'language',
        },
        options: {
          flag: 'product.country.id',
        },
        displayed: true,
      },
      {
        columnDef: 'packs',
        header: 'Packs',
        displayed: true,
        link: {
          resource: 'packs',
          list: 'packoffers',
          target: 'packoffers.offer',
        },
      },
      {
        columnDef: 'service',
        displayedDatas: [
          {
            path: ['product.service.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Service',
        link: {
          resource: 'services',
          filter: 'product.service.id',
          icon: 'shopping_basket',
        },
        displayed: false,
      },
      {
        columnDef: 'platform',
        displayedDatas: [
          {
            path: ['platform.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Platform',
        link: {
          resource: 'platforms',
          filter: 'platform.id',
          icon: 'dns',
        },
        displayed: false,
      },
      {
        columnDef: 'trial',
        displayedDatas: [
          {
            path: ['trialPrice'],
            addUnit: {
              path: 'currency.id',
            },
            nullIfEmpty: true,
          },
          {
            path: ['trialIteration'],
            class: 'mat-body-2',
            addUnit: {
              path: 'trialperiod.name',
            },
            nullIfEmpty: true,
          },
        ],
        header: 'Trial',
        displayed: false,
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'business',
      options: {
        flag: 'country.id',
      },
      title: [
        {
          path: ['name'],
        },
      ],
      subtitle: [
        {
          path: [
            'id',
            'country.id',
            'billingmode.name',
            'recurringperiod.name',
          ],
          translatable: false,
        },
      ],
      validity: true,
    },
    elements: [
      {
        showEmpty: true,
        columns: true,
        list: [
          {
            icon: 'language',
            title: 'Product',
            APIKey: 'product',
            value: {
              path: ['product.name'],
            },
            link: {
              resource: 'products',
              filter: 'product.id',
            },
            options: {
              flag: 'country.id',
            },
          },
          {
            title: 'Commerical Name',
            icon: 'badge',
            APIKey: 'commercialName',
            value: {
              path: ['commercialName'],
            },
          },
          {
            icon: 'inventory',
            title: 'Packs',
            APIKey: 'packs',
            value: {
              path: ['packs'],
            },
            link: {
              resource: 'packs',
              list: 'packs',
              target: 'packoffers.offer',
            },
          },
          {
            icon: 'category',
            title: 'Business Unit',
            APIKey: 'businessunits',
            value: {
              path: ['name'],
              type: 'chips',
            },
          },
          {
            icon: 'attach_money',
            title: 'Price',
            value: {
              path: ['price'],
              addUnit: {
                path: 'currency.id',
              },
            },
          },
          {
            title: 'Trial',
            value: {
              path: [
                'trialPrice',
                'currency.id',
                'trialIteration',
                'trialperiod.name',
              ],
            },
          },
        ],
      },
    ],
    tabs: [
      {
        title: 'Billing',
        elements: [
          {
            showEmpty: true,
            columns: true,
            title: 'Provider',
            list: [
              {
                icon: 'signal_cellular_alt',
                title: 'End User Provider (Firm - Job)',
                APIKey: 'enduserproviderjob',
                value: {
                  path: [
                    'enduserproviderjob.firm.name',
                    'enduserproviderjob.firm.headquarterscountry.id',
                    'enduserproviderjob.activity.name',
                  ],
                  translatable: true,
                },
                link: {
                  resource: 'firms',
                  filter: 'enduserproviderjob.firm.id',
                },
              },
              {
                icon: 'business',
                title: 'Billing Provider',
                APIKey: 'billingproviderjob',
                value: {
                  path: ['billingproviderjob.firm.name'],
                },
                link: {
                  resource: 'firms',
                  filter: 'billingproviderjob.firm.id',
                },
              },
              {
                icon: 'dns',
                title: 'Platform',
                APIKey: 'platform',
                value: {
                  path: ['platform.name'],
                },
                link: {
                  resource: 'platforms',
                  filter: 'platform.id',
                },
              },
              {
                icon: 'money',
                title: 'Billing Channel',
                APIKey: 'billingchannel',
                value: {
                  path: ['billingchannel.name'],
                },
              },
            ],
          },
          {
            columns: true,
            showEmpty: true,
            title: 'Offer Type',
            list: [
              {
                icon: 'money',
                title: 'Billing Mode',
                value: {
                  path: ['billingmode.name'],
                },
              },
              {
                icon: 'settings',
                title: 'Subscription Context',
                value: {
                  path: ['subscriptionContext'],
                },
              },
              {
                icon: 'restore',
                title: 'Recurring Period (case subscription)',
                value: {
                  path: ['recurringIteration', 'recurringperiod.name'],
                },
              },
              {
                icon: 'timelapse',
                title: 'Duration Period',
                value: {
                  path: ['durationIteration', 'durationperiod'],
                },
              },
              {
                icon: 'credit_card',
                title: 'Credit Setup',
                value: {
                  path: ['ceditsetup.name'],
                },
              },
            ],
          },
          {
            showEmpty: true,
            title: 'Handler Firm',
            list: [
              {
                icon: 'business',
                APIKey: 'handlerfirm',
                value: {
                  path: [
                    'handlerfirm.name',
                    'handlerfirm.headquarterscountry.id',
                  ],
                },
                link: {
                  resource: 'firms',
                  filter: 'handlerfirm.id',
                },
                inherit: {
                  resourceKey: 'product',
                  path: 'name',
                  from: 'product',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Technical Setup',
        elements: [
          {
            list: [
              {
                type: 'offercapabilities',
              },
            ],
          },
        ],
      },
      {
        title: 'Billing Policies',
        elements: [
          {
            resource: {
              APIKey: 'billingpolicies',
              config: new OfferBillingPolicyConfig(this.atom),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Messages',
        elements: [
          {
            resource: {
              APIKey: 'messages',
              config: new MessageConfig(this.atom, 'offer'),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Linked & Data',
        elements: [
          {
            showEmpty: true,
            list: [
              {
                icon: 'shopping_basket',
                title: 'Service',
                APIKey: 'service',
                value: {
                  path: ['product.service.name'],
                },
                link: {
                  resource: 'services',
                  filter: 'product.service.id',
                },
              },
              {
                title: 'Country',
                APIKey: 'country',
                value: {
                  path: ['country.id', 'country.name'],
                },
                link: {
                  resource: 'countries',
                  filter: 'country.id',
                },
                options: {
                  flag: 'country.id',
                },
              },
              {
                type: 'data',
                APIKey: 'data',
                value: {
                  path: ['data'],
                },
              },
            ],
          },
        ],
      },
    ],
  };
  wizard = {
    noCloseAfterCreation: true,
    tabs: [
      {
        title: 'Providers',
        formGroup: 'general',
        list: [
          {
            type: 'text',
            title: 'Name',
            APIKey: 'name',
            formControl: 'name',
            required: true,
            translatable: true,
          },
          {
            type: 'text',
            title: 'Commerical Name',
            formControl: 'commercialName',
            required: true,
            translatable: true,
          },
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            APIKey: 'country',
            search: new CountryConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'billingchannel',
              },
              {
                group: 'general',
                control: 'enduserproviderjob',
                filters: {
                  filter: 'like.firm.headquarterscountry.id',
                },
              },
              {
                group: 'general',
                control: 'product',
                filters: {
                  filter: 'like.country.id',
                },
              },
              {
                group: 'offertype',
                control: 'currency',
                filters: {
                  filter: 'like.countries.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Product',
            formControl: 'product',
            APIKey: 'product',
            search: new ProductConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'offertype',
                control: 'creditsetup',
                type: 'resource',
                condition: {
                  path: 'withCredit',
                  include: ['true'],
                },
              },
              {
                group: 'general',
                control: 'handlerfirm',
                type: 'resource',
                inherit: true,
              },
            ],
          },
          {
            type: 'resource',
            title: 'Billing Channel',
            formControl: 'billingchannel',
            APIKey: 'billingchannel',
            search: new BillingChannelConfig(),
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'enduserproviderjob',
                routing: {
                  '1': '2',
                  '2': '2',
                  '3': '2',
                  '4': '3',
                  '5': '4',
                  '6': '4',
                  '7': '4',
                  filter: 'equal.activity.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Enduser Provider',
            formControl: 'enduserproviderjob',
            APIKey: 'enduserproviderjob',
            search: new FirmJobConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'platform',
                filters: {
                  filter: 'equal.platformcapabilities.enduserproviderjob.id',
                },
              },
              {
                group: 'general',
                control: 'billingproviderjob',
                filters: {
                  filter:
                    'equal.platforms.platformcapabilities.enduserproviderjob.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Billing Provider',
            formControl: 'billingproviderjob',
            APIKey: 'billingproviderjob',
            search: new FirmJobConfig(this.atom),
            required: true,
            filter: [
              {
                key: 'equal.activity.id',
                fixValue: '1',
              },
            ],
            activatingSequence: [
              {
                group: 'general',
                control: 'platform',
                filters: {
                  filter: 'equal.job.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Platform',
            formControl: 'platform',
            APIKey: 'platform',
            search: new PlatformConfig(this.atom),
            required: true,
          },
        // ],
        // advance: [
        ],
      },
      {
        title: 'Offer Type',
        formGroup: 'offertype',
        list: [
          {
            type: 'fixlist',
            title: 'Subscription Context',
            formControl: 'subscriptionContext',
            APIKey: 'subscriptionContext',
            required: true,
            fixlistValues: [
              { code: 'none', label: 'None' },
              { code: 'service', label: 'Service' },
              { code: 'product', label: 'Product' },
              { code: 'offer', label: 'Offer' },
            ],
          },
          {
            type: 'resource',
            title: 'Credit Setup',
            formControl: 'creditsetup',
            APIKey: 'creditsetup',
            search: new CreditsetupConfig(),
            required: true,
          },
          {
            type: 'resource',
            title: 'Billing Mode',
            formControl: 'billingmode',
            APIKey: 'billingmode',
            search: new BillingModeConfig(),
            required: true,
            noCols: true,
            activatingSequence: [
              {
                group: 'offertype',
                control: 'recurringIteration',
                type: 'resource',
                required: true,
                condition: {
                  include: ['2'],
                },
              },
              {
                group: 'offertype',
                control: 'recurringperiod',
                type: 'resource',
                required: true,
                condition: {
                  include: ['2'],
                },
              },
            ],
          },
          {
            type: 'number',
            title: 'Recurring Iteration',
            APIKey: 'recurringIteration',
            formControl: 'recurringIteration',
            required: true,
          },
          {
            type: 'resource',
            title: 'Recurring Period',
            formControl: 'recurringperiod',
            APIKey: 'recurringperiod',
            search: new PeriodConfig(),
            required: true,
          },
          {
            type: 'number',
            title: 'Price',
            APIKey: 'price',
            formControl: 'price',
            required: true,
          },
          {
            type: 'resource',
            title: 'Currency',
            formControl: 'currency',
            APIKey: 'currency',
            search: new CurrencyConfig(),
            required: true,
          },
          {
            type: 'number',
            title: 'Duration Iteration',
            APIKey: 'durationIteration',
            formControl: 'durationIteration',
            activatingSequence: [
              {
                group: 'offertype',
                control: 'durationperiod',
              },
            ],
          },
          {
            type: 'resource',
            title: 'Duration Period',
            formControl: 'durationperiod',
            APIKey: 'durationperiod',
            search: new PeriodConfig(),
            required: true,
          },
        ],
      },
      {
        title: 'Trial',
        formGroup: 'trial',
        list: [
          {
            type: 'number',
            title: 'Trial Price',
            APIKey: 'trialPrice',
            formControl: 'trialPrice',
            noCols: true,
            activatingSequence: [
              {
                group: 'trial',
                control: 'trialIteration',
              },
              {
                group: 'trial',
                control: 'trialperiod',
              },
            ],
          },
          {
            type: 'number',
            title: 'Trial Iteration',
            APIKey: 'trialIteration',
            formControl: 'trialIteration',
            required: true,
          },
          {
            type: 'resource',
            title: 'Trial Period',
            formControl: 'trialperiod',
            APIKey: 'trialperiod',
            search: new PeriodConfig(),
            required: true,
          },
        ],
      },
      {
        title: 'Data',
        formGroup: 'data',
        list: [
          {
            type: 'data',
            title: 'Data',
            formControl: 'data',
          },
          {
            type: 'resource',
            title: 'Handler Firm',
            formControl: 'handlerfirm',
            APIKey: 'handlerfirm',
            search: new FirmConfig(this.atom),
            noCols: true,
            inherit: {
              resourceKey: 'handlerfirm',
              path: 'name',
              from: 'Product',
            },
          },
        ]
      }
    ],
  };
  resource = Offer;
  resourceName = 'Offer';
  resourceKey = 'offers';
  resourceKeySingle = 'offer';
  includes = [
    'country',
    'currency',
    'billingchannel',
    'billingmode',
    'enduserproviderjob.firm.headquarterscountry',
    'enduserproviderjob.activity',
    'billingproviderjob.firm.headquarterscountry',
    'billingproviderjob.activity',
    'product.service',
    'product.country',
    'packoffers',
    'recurringperiod',
    'businessunits',
    'trialperiod',
    'durationperiod',
    'handlerfirm.headquarterscountry',
    'platform',
    'enumsubscriptioncontext',
    'messages.platform.job.firm',
    'messages.enumtype',
    'messages.tags',
    'offercapabilities.orderchannel',
    'offercapabilities.networkchannel',
    'offercapabilities.billingkinematic',
    'offercapabilities.sku',
    'offercapabilities.catalog',
    'offercapabilities.contract.paymentdimensions.orderchannel',
    'offercapabilities.contract.paymentdimensions.networkchannel',
    'offercapabilities.contract.paymentdimensions.billingkinematic',
    'offercapabilities.contract.paymentdimensions.billingchannel',
    'offercapabilities.contract.paymentdimensions.billingproviderjob',
    'offercapabilities.contract.paymentdimensions.enduserproviderjob',
    'offercapabilities.contract.paymentdimensions.country',
    'offercapabilities.contract.paymentdimensions.platform',
    'offerbillingpolicy.billingpolicy',
    'creditsetup',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name'),
    new StringFilter('country.id', 'Country ID', true),
    new StringFilter('country.name', 'Country name'),
    new NumberFilter('product.id', 'Product ID'),
    new StringFilter('product.name', 'Product name'),
    new NumberFilter('product.service.id', 'Service ID'),
    new StringFilter('product.service.name', 'Service name'),
    new StringFilter('currency.id', 'Currency ID'),
    new StringFilter('currency.name', 'Currency name'),
    new NumberFilter('price', 'Price'),
    new ResourceListFilter(
      this.atom,
      Period,
      'recurringperiod.id',
      'Recurring period'
    ),
    new ResourceListFilter(
      this.atom,
      BillingMode,
      'billingmode.id',
      'Billing mode'
    ),
    new ResourceListFilter(
      this.atom,
      BillingChannel,
      'billingchannel.id',
      'Billing channel'
    ),
    new StringFilter('billingproviderjob.firm.name', 'Billing Provider'),
    new StringFilter('enduserproviderjob.firm.name', 'End User Provider'),
    new NumberFilter('enduserproviderjob.id', 'End User Provider ID'),
    new NumberFilter('platform.id', 'Platform ID'),
    new StringFilter('platform.name', 'Platform name'),
    new ResourceListFilter(
      this.atom,
      BusinessUnit,
      'businessunits.id',
      'Business unit'
    ),
    new StringFilter('offercapabilities.sku.external_id', 'SKU external ID'),
    new NumberFilter('packoffers.pack.id', 'Pack ID'),
    new StringFilter('packoffers.pack.name', 'Pack name'),
    new NumberFilter(
      'offerbillingpolicy.billingpolicy.id',
      'Billing policy ID'
    ),
    new StringFilter(
      'offerbillingpolicy.billingpolicy.name',
      'Billing policy name'
    ),
    new NumberFilter('product.service.ownerfirm.id', 'Owner Firm ID'),
    new StringFilter('product.service.ownerfirm.name', 'Owner Firm name'),
    new NumberFilter('creditsetup.id', 'Credit Setup ID'),
  ];
  constructor(private atom: AtomService) {}
}
